import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output,
} from '@angular/core';
import { EnrichedCashbackFormData } from '@features/cashback/types';
import { TransactionDetailsFormatterService } from '@rd/core/services/transaction-details-formatter/transaction-details-formatter.service';
import { LoyaltyProgramHelper } from '@rd/helpers/loyalty-program.helper';
import { DynamicListItem } from '@rd/shared-legacy/components/dynamic-list/dynamic-list-item.type';
import {
  CashBackMembership,
  CashBackToAccountMembership,
  CashBackToCardMembership,
  CashBackToPlatformMembership,
  HandleApplyPromocodeData,
  HttpError,
  LoyaltyProgram,
  LoyaltyProgramCategory,
  SubmitCashRedemptionData,
} from '@rd/types';
import { Promocode, PromocodeUsage } from '@rd/types/promocode.type';
import { RedeemCashSettingService } from '../../services/redeem-cash-setting/redeem-cash-setting.service';

@Component({
  selector: 'rd-rcv2-review-body',
  templateUrl: './review-body.component.html',
  styleUrls: ['./review-body.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RcV2ReviewBodyComponent {
  @HostBinding('class.rd-rcv2-review-body') componentClass = true;

  readonly LoyaltyProgramCategory = LoyaltyProgramCategory;

  @Input() formData: EnrichedCashbackFormData;
  @Input() redeemCashLoyaltyProgram: LoyaltyProgram;
  @Input() cashRedemptionLoading: boolean;
  @Input() redeemCashMembership: CashBackMembership;
  @Input() promocodeLoading = false;
  @Input() promocode: Promocode | null;
  @Input() promocodeUsage: PromocodeUsage | null;
  @Input() promocodeHttpError: HttpError | null;
  @Output() submitCashRedemption = new EventEmitter<SubmitCashRedemptionData>();
  @Output() applyPromocode = new EventEmitter<HandleApplyPromocodeData>();

  constructor(
    private redeemCashSettingService: RedeemCashSettingService,
    private transactionDetailsFormatterService: TransactionDetailsFormatterService,
    private loyaltyProgramHelper: LoyaltyProgramHelper,
  ) {}

  submitCashRedemptionEvent(): void {
    this.submitCashRedemption.emit({
      loyaltyProgramId: this.redeemCashLoyaltyProgram?.id,
      formData: this.formData,
      promocode: this.promocode ?? undefined,
      promocodeUsage: this.promocodeUsage ?? undefined,
    });
  }

  handleApplyPromocode(promocode?: string): void {
    this.applyPromocode.emit({
      loyaltyProgramId: this.redeemCashLoyaltyProgram?.id,
      formData: this.formData,
      promocode,
    });
  }

  get shouldDisplayMembershipDetailsOnReviewPage(): boolean {
    return this.redeemCashSettingService
      .shouldDisplayMembershipDetailsOnReviewPage;
  }

  get redeemToAccountDetails(): DynamicListItem[] {
    return this.transactionDetailsFormatterService.redeemCashToAccountDetails(
      this.redeemCashMembership as CashBackToAccountMembership,
    );
  }

  get redeemToCardDetails(): DynamicListItem[] {
    return this.transactionDetailsFormatterService.redeemCashToCardDetails(
      this.redeemCashMembership as CashBackToCardMembership,
    );
  }

  get redeemToPlatformDetails(): DynamicListItem[] {
    return this.transactionDetailsFormatterService.redeemCashToPlatformDetails(
      this.redeemCashMembership as CashBackToPlatformMembership,
    );
  }

  get processingDelay(): string {
    return this.redeemCashLoyaltyProgram
      ? this.loyaltyProgramHelper.transferTime(
          this.loyaltyProgramHelper.getCashBackCapability(
            this.redeemCashLoyaltyProgram,
          ).processingDelay,
        )
      : '';
  }

  get groupName(): string {
    return (this.redeemCashMembership as CashBackToCardMembership).details
      .groupName;
  }

  get bsbNumber(): string {
    return (this.redeemCashMembership as CashBackToAccountMembership).details
      .bsbNumber;
  }

  get hasPromocode(): boolean {
    return this.redeemCashSettingService.hasPromocode;
  }
}
